/**
 * Which stored Gemini Live thinking level a session actually SENDS.
 *
 * A level stored on its own is not enough. Until the relay began sending
 * `generationConfig.thinkingConfig.thinkingLevel`, nothing sent a stored
 * level: every 3.1 call ran at Google's default, MINIMAL ("The default is
 * minimal to optimize for lowest latency." —
 * https://ai.google.dev/gemini-api/docs/models/gemini-3.1-flash-live-preview),
 * whatever the picker had saved. A deployment that stored `high` has been
 * answering its callers at MINIMAL ever since, so sending that `high` now
 * would add reasoning latency to calls nobody changed.
 *
 * So a level is sent only when `geminiThinkingLevelChosenAt` sits beside it.
 * A level stored without it predates the send: it is kept, never applied, and
 * shown to the operator as a choice that did not reach the model, until someone
 * picks a level again. Migrating those rows is a decision about customers — a
 * data change — never a side effect of this rule.
 *
 * The marker is the SERVER's, written on the deployment write path whatever the
 * client — editor, public API, CLI, SDK — by {@link stampGeminiThinkingLevelOnBirth}
 * for a row being created and by the settings patch statement for every update,
 * both spelling the same rule:
 *
 *   - no level                                  → no marker
 *   - the level changed                         → stamped now
 *   - the level is unchanged and already marked → the stored marker, untouched
 *   - the level is unchanged and never marked   → stamped now only if the write
 *     itself carries a marker (the editor's re-pick of a level it shows as never
 *     applied); otherwise it stays unmarked, so an unrelated save cannot apply it
 *
 * A marker a client sends is only ever that last signal: its value is never
 * stored.
 */

import { z } from "zod";

import { NESTED_VOICE_CONFIG_KEY } from "../voice-config-sites";
import { type GeminiLiveThinkingLevel } from "./GeminiLiveTunables.schema";

/**
 * When an operator last picked the thinking level through a surface that sends
 * it. ISO-8601. Read leniently: a malformed marker reads as absent, so the
 * level it would have unlocked stays unsent rather than failing the whole
 * settings parse.
 */
export const GeminiThinkingLevelChosenAtSchema = z.string().datetime().optional().catch(undefined);

/** The stored key names, shared by both spellings of the stamp rule. */
export const GEMINI_THINKING_LEVEL_KEY = "geminiThinkingLevel" as const;
export const GEMINI_THINKING_LEVEL_CHOSEN_AT_KEY = "geminiThinkingLevelChosenAt" as const;

/** The two voice-config fields this rule reads. */
export interface GeminiThinkingLevelFields {
  geminiThinkingLevel?: GeminiLiveThinkingLevel;
  geminiThinkingLevelChosenAt?: string;
}

export type GeminiThinkingLevelChoice =
  /** No level stored — the model's own default applies. */
  | { state: "unset" }
  /** Picked through a surface that sends it — this level goes on the wire. */
  | { state: "chosen"; level: GeminiLiveThinkingLevel }
  /** Stored before any level was sent — kept, never sent. */
  | { state: "never-applied"; stored: GeminiLiveThinkingLevel };

export function readGeminiThinkingLevelChoice(
  fields: GeminiThinkingLevelFields
): GeminiThinkingLevelChoice {
  const level = fields.geminiThinkingLevel;
  if (level === undefined) return { state: "unset" };
  if (fields.geminiThinkingLevelChosenAt === undefined) {
    return { state: "never-applied", stored: level };
  }
  return { state: "chosen", level };
}

/** The level a session sends, or `undefined` for the model's own default. */
export function geminiThinkingLevelToSend(
  fields: GeminiThinkingLevelFields
): GeminiLiveThinkingLevel | undefined {
  const choice = readGeminiThinkingLevelChoice(fields);
  return choice.state === "chosen" ? choice.level : undefined;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** One voice config, stamped as a row being BORN: nothing is stored to compare. */
function stampSiteOnBirth(site: Record<string, unknown>, nowIso: string): Record<string, unknown> {
  const { [GEMINI_THINKING_LEVEL_CHOSEN_AT_KEY]: _clientMarker, ...rest } = site;
  const level = site[GEMINI_THINKING_LEVEL_KEY];
  return typeof level === "string"
    ? { ...rest, [GEMINI_THINKING_LEVEL_CHOSEN_AT_KEY]: nowIso }
    : rest;
}

/**
 * The marker on a deployment's settings as the row is CREATED, at both places a
 * voice config sits (`voiceConfigSites`): the root, and `voiceSettings`.
 *
 * A born row has no stored level, so a level it carries is by definition a
 * change and is stamped with `nowIso`; no level means no marker. A marker the
 * client sent is replaced, never kept. Everything else passes through
 * untouched, and a settings value that is not an object is returned as is.
 *
 * A DUPLICATE is not a birth in this sense and must not come through here: the
 * copy runs exactly as its source did, marker included.
 */
export function stampGeminiThinkingLevelOnBirth(settings: unknown, nowIso: string): unknown {
  if (!isRecord(settings)) return settings;
  const root = stampSiteOnBirth(settings, nowIso);
  const nested = root[NESTED_VOICE_CONFIG_KEY];
  return isRecord(nested)
    ? { ...root, [NESTED_VOICE_CONFIG_KEY]: stampSiteOnBirth(nested, nowIso) }
    : root;
}
